import {useEffect, useState} from "react";
import {connect} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css, Theme} from "@emotion/react";
import lscache from "lscache";
import {bindActionCreators, Dispatch} from "redux";

import {WideContent} from "@pg-design/grid-module";
import {borderRadius, flex, flexAbsoluteCenter, mb, mt, ph, pt, pv} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Tabs} from "@pg-design/tabs";
import {Text} from "@pg-design/text-module";
import {createFormActions, createFormActionTypes, FormActions} from "@pg-mono/form";

import {scrollToTop} from "../../app/components/ScrollToTop";
import {PROPERTY_CONFIGURATOR_LOCATION} from "../../app/constants/storage_keys";
import {IRPStore} from "../../app/rp_reducer";
import {tabsWrapper} from "../../atoms/TabsWrapper";
import {googlePlacesDivId} from "../../search/utils/search_constants";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {GTMEvent} from "../analytics/events";
import {BudgetTab} from "../components/BudgetTab";
import {LocationTab} from "../components/LocationTab";
import {RoomsTab} from "../components/RoomsTab";
import {usePropertyConfiguratorFiltersAnalytics} from "../hooks/use_property_configurator_filters_analytics";
import {usePropertyConfiguratorStepAnalytics} from "../hooks/use_property_configurator_step_analytics";
import {searchFormFields} from "../reducers/property_configurator_reducer";
import {getPropertyConfiguratorUrl} from "../utils/get_url_with_params";
import {parseQueryParams} from "../utils/parse_query_params";
import {getPropertyConfiguratorStorageData, setPropertyConfiguratorStorageData} from "../utils/property_configurator_storage";
import {ResultsList} from "./ResultsList";

export enum PropertyConfiguratorStep {
    LOCATION = 1,
    ROOMS = 2,
    BUDGET = 3,
    RESULTS = 4
}

export const configSteps = [
    {
        title: "Lokalizacja",
        value: PropertyConfiguratorStep.LOCATION
    },
    {
        title: "Pomieszczenia i piętro",
        value: PropertyConfiguratorStep.ROOMS
    },
    {
        title: "Budżet",
        value: PropertyConfiguratorStep.BUDGET
    }
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SearchFormValuesType = Record<keyof typeof searchFormFields, any> & {
    utm_campaign?: string;
    utm_content?: string;
    utm_id?: string;
    utm_medium?: string;
    utm_source?: string;
    utm_term?: string;
    fbclid?: string;
};

export interface IStateProps {
    formValues: SearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
}

interface IProps extends IStateProps, IActionsProps {}
export const PropertyConfiguratorC = (props: IProps) => {
    const history = useHistory();
    const location = useLocation();
    const {trackAftermarket, trackLoan, trackLocalisation, trackPrice} = usePropertyConfiguratorFiltersAnalytics(GTMEvent.FLAT_CONFIG_MODAL);

    const [currentStep, setCurrentStep] = useState<null | PropertyConfiguratorStep>(null);

    const handleSaveValuesToLS = (values: SearchFormValuesType) => {
        history.push(getPropertyConfiguratorUrl(values));
        setPropertyConfiguratorStorageData({
            ...values,
            search: {
                ...values.search,
                label: ""
            }
        });
    };

    useEffect(() => {
        scrollToTop();
    }, [currentStep]);

    const trackStepChange = usePropertyConfiguratorStepAnalytics();

    useEffect(() => {
        const parsedQueryValues = parseQueryParams(location.search);
        const lsValues = getPropertyConfiguratorStorageData();
        const values = location.search ? parsedQueryValues : lsValues;
        const propertyConfiguratorLocation = lscache.get(PROPERTY_CONFIGURATOR_LOCATION) || {};

        if (Object.keys(values).length) {
            setTimeout(() => {
                props.formActions.update({...values, ...(propertyConfiguratorLocation?.regions?.length && {search: propertyConfiguratorLocation})});
                if (propertyConfiguratorLocation?.regions?.length) {
                    lscache.remove(PROPERTY_CONFIGURATOR_LOCATION);
                }
                setCurrentStep(PropertyConfiguratorStep.RESULTS);
            }, 1);
        } else {
            setCurrentStep(PropertyConfiguratorStep.LOCATION);
            trackStepChange(PropertyConfiguratorStep.LOCATION);
        }
    }, []);

    useEffect(() => {
        if (currentStep === PropertyConfiguratorStep.RESULTS) {
            handleSaveValuesToLS(props.formValues);
        }
    }, [currentStep]);

    const onSetCurrentStep = (step: PropertyConfiguratorStep, prev = false) => {
        if (currentStep === PropertyConfiguratorStep.LOCATION) {
            trackLocalisation();
        }
        if (currentStep === PropertyConfiguratorStep.BUDGET) {
            trackAftermarket();
            trackLoan();
            trackPrice();
        }
        if (!prev) {
            setTimeout(() => trackStepChange(step), 1);
        }
        setCurrentStep(step);
    };

    const stepRenderer = () => {
        switch (currentStep) {
            case PropertyConfiguratorStep.LOCATION:
                return <LocationTab handleTabChange={onSetCurrentStep} />;
            case PropertyConfiguratorStep.ROOMS:
                return <RoomsTab handleTabChange={onSetCurrentStep} values={props.formValues} onChange={props.formActions.update} />;
            case PropertyConfiguratorStep.BUDGET:
                return <BudgetTab handleTabChange={onSetCurrentStep} values={props.formValues} onChange={props.formActions.update} />;
        }
    };

    if (!currentStep) {
        return (
            <WideContent css={noStep}>
                <Loader padding="xxl" size="lg" />
            </WideContent>
        );
    }

    return (
        <>
            <div id={googlePlacesDivId} />
            {currentStep === PropertyConfiguratorStep.RESULTS ? (
                <ResultsList handleStoreValues={handleSaveValuesToLS} />
            ) : (
                <WideContent css={contentStyle}>
                    <Text variant="headline_3" mb="3.2rem">
                        Konfigurator mieszkania
                    </Text>
                    <div css={tabsWrapper}>
                        <Tabs activeColor="gray" fullWidth color="gray" tabs={configSteps} value={currentStep} variant="contained" onChange={setCurrentStep} />
                    </div>
                    <div css={whiteWrapper}>{stepRenderer()}</div>
                </WideContent>
            )}
        </>
    );
};

export const PropertyConfigurator = connect(mapStateToProps, mapActionsToProps)(PropertyConfiguratorC);

const search_actions = "property_configurator/SEARCH_FORM";
export const searchFormTypes = createFormActionTypes(search_actions);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const searchFormActions = createFormActions<Record<keyof typeof searchFormFields, any>>(searchFormTypes);

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const whiteWrapper = (theme: Theme) => css`
    background-color: #fff;
    ${ph(4)};
    ${pv(4)};
    ${borderRadius(4)};
    ${mt(3)};
    ${flex("flex-start")};
    flex-direction: column;

    @media (max-width: ${theme.breakpoints.md}) {
        ${ph(2)};
    }
`;

const contentStyle = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
    ${mb(8)};
    ${pt(8)};
    ${ph(0)};

    @media (max-width: ${theme.breakpoints.md}) {
        ${pt(3)};
    }
`;

const noStep = css`
    ${flexAbsoluteCenter};
    min-height: 100%;
`;
import {useEffect, useState} from "react";
import {connect} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css, Theme} from "@emotion/react";
import lscache from "lscache";
import {bindActionCreators, Dispatch} from "redux";

import {WideContent} from "@pg-design/grid-module";
import {borderRadius, flex, flexAbsoluteCenter, mb, mt, ph, pt, pv} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {Tabs} from "@pg-design/tabs";
import {Text} from "@pg-design/text-module";
import {createFormActions, createFormActionTypes, FormActions} from "@pg-mono/form";

import {scrollToTop} from "../../app/components/ScrollToTop";
import {PROPERTY_CONFIGURATOR_LOCATION} from "../../app/constants/storage_keys";
import {IRPStore} from "../../app/rp_reducer";
import {tabsWrapper} from "../../atoms/TabsWrapper";
import {googlePlacesDivId} from "../../search/utils/search_constants";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {GTMEvent} from "../analytics/events";
import {BudgetTab} from "../components/BudgetTab";
import {LocationTab} from "../components/LocationTab";
import {RoomsTab} from "../components/RoomsTab";
import {usePropertyConfiguratorFiltersAnalytics} from "../hooks/use_property_configurator_filters_analytics";
import {usePropertyConfiguratorStepAnalytics} from "../hooks/use_property_configurator_step_analytics";
import {searchFormFields} from "../reducers/property_configurator_reducer";
import {getPropertyConfiguratorUrl} from "../utils/get_url_with_params";
import {parseQueryParams} from "../utils/parse_query_params";
import {getPropertyConfiguratorStorageData, setPropertyConfiguratorStorageData} from "../utils/property_configurator_storage";
import {ResultsList} from "./ResultsList";

export enum PropertyConfiguratorStep {
    LOCATION = 1,
    ROOMS = 2,
    BUDGET = 3,
    RESULTS = 4
}

export const configSteps = [
    {
        title: "Lokalizacja",
        value: PropertyConfiguratorStep.LOCATION
    },
    {
        title: "Pomieszczenia i piętro",
        value: PropertyConfiguratorStep.ROOMS
    },
    {
        title: "Budżet",
        value: PropertyConfiguratorStep.BUDGET
    }
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SearchFormValuesType = Record<keyof typeof searchFormFields, any> & {
    utm_campaign?: string;
    utm_content?: string;
    utm_id?: string;
    utm_medium?: string;
    utm_source?: string;
    utm_term?: string;
    fbclid?: string;
};

export interface IStateProps {
    formValues: SearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
}

interface IProps extends IStateProps, IActionsProps {}
export const PropertyConfiguratorC = (props: IProps) => {
    const history = useHistory();
    const location = useLocation();
    const {trackAftermarket, trackLoan, trackLocalisation, trackPrice} = usePropertyConfiguratorFiltersAnalytics(GTMEvent.FLAT_CONFIG_MODAL);

    const [currentStep, setCurrentStep] = useState<null | PropertyConfiguratorStep>(null);

    const handleSaveValuesToLS = (values: SearchFormValuesType) => {
        history.push(getPropertyConfiguratorUrl(values));
        setPropertyConfiguratorStorageData({
            ...values,
            search: {
                ...values.search,
                label: ""
            }
        });
    };

    useEffect(() => {
        scrollToTop();
    }, [currentStep]);

    const trackStepChange = usePropertyConfiguratorStepAnalytics();

    useEffect(() => {
        const parsedQueryValues = parseQueryParams(location.search);
        const lsValues = getPropertyConfiguratorStorageData();
        const values = location.search ? parsedQueryValues : lsValues;
        const propertyConfiguratorLocation = lscache.get(PROPERTY_CONFIGURATOR_LOCATION) || {};

        if (Object.keys(values).length) {
            setTimeout(() => {
                props.formActions.update({...values, ...(propertyConfiguratorLocation?.regions?.length && {search: propertyConfiguratorLocation})});
                if (propertyConfiguratorLocation?.regions?.length) {
                    lscache.remove(PROPERTY_CONFIGURATOR_LOCATION);
                }
                setCurrentStep(PropertyConfiguratorStep.RESULTS);
            }, 1);
        } else {
            setCurrentStep(PropertyConfiguratorStep.LOCATION);
            trackStepChange(PropertyConfiguratorStep.LOCATION);
        }
    }, []);

    useEffect(() => {
        if (currentStep === PropertyConfiguratorStep.RESULTS) {
            handleSaveValuesToLS(props.formValues);
        }
    }, [currentStep]);

    const onSetCurrentStep = (step: PropertyConfiguratorStep, prev = false) => {
        if (currentStep === PropertyConfiguratorStep.LOCATION) {
            trackLocalisation();
        }
        if (currentStep === PropertyConfiguratorStep.BUDGET) {
            trackAftermarket();
            trackLoan();
            trackPrice();
        }
        if (!prev) {
            setTimeout(() => trackStepChange(step), 1);
        }
        setCurrentStep(step);
    };

    const stepRenderer = () => {
        switch (currentStep) {
            case PropertyConfiguratorStep.LOCATION:
                return <LocationTab handleTabChange={onSetCurrentStep} />;
            case PropertyConfiguratorStep.ROOMS:
                return <RoomsTab handleTabChange={onSetCurrentStep} values={props.formValues} onChange={props.formActions.update} />;
            case PropertyConfiguratorStep.BUDGET:
                return <BudgetTab handleTabChange={onSetCurrentStep} values={props.formValues} onChange={props.formActions.update} />;
        }
    };

    if (!currentStep) {
        return (
            <WideContent css={noStep}>
                <Loader padding="xxl" size="lg" />
            </WideContent>
        );
    }

    return (
        <>
            <div id={googlePlacesDivId} />
            {currentStep === PropertyConfiguratorStep.RESULTS ? (
                <ResultsList handleStoreValues={handleSaveValuesToLS} />
            ) : (
                <WideContent css={contentStyle}>
                    <Text variant="headline_3" mb="3.2rem">
                        Konfigurator mieszkania
                    </Text>
                    <div css={tabsWrapper}>
                        <Tabs activeColor="gray" fullWidth color="gray" tabs={configSteps} value={currentStep} variant="contained" onChange={setCurrentStep} />
                    </div>
                    <div css={whiteWrapper}>{stepRenderer()}</div>
                </WideContent>
            )}
        </>
    );
};

export const PropertyConfigurator = connect(mapStateToProps, mapActionsToProps)(PropertyConfiguratorC);

const search_actions = "property_configurator/SEARCH_FORM";
export const searchFormTypes = createFormActionTypes(search_actions);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const searchFormActions = createFormActions<Record<keyof typeof searchFormFields, any>>(searchFormTypes);

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const whiteWrapper = (theme: Theme) => css`
    background-color: #fff;
    ${ph(4)};
    ${pv(4)};
    ${borderRadius(4)};
    ${mt(3)};
    ${flex("flex-start")};
    flex-direction: column;

    @media (max-width: ${theme.breakpoints.md}) {
        ${ph(2)};
    }
`;

const contentStyle = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
    ${mb(8)};
    ${pt(8)};
    ${ph(0)};

    @media (max-width: ${theme.breakpoints.md}) {
        ${pt(3)};
    }
`;

const noStep = css`
    ${flexAbsoluteCenter};
    min-height: 100%;
`;
